import { TenorFieldData } from 'components/shared/TenorField/TenorField';
import { IntlKey } from 'locales';
import { CurrencyListFieldMetadata, DateFieldMetadata, FieldMetadata, ListFieldMetadata } from 'models/forms/fields';
import { ISalesCounterpart } from 'models/ISalesCounterpart';
import { Underlying } from 'models/underlying';
import { toCurrency } from 'utils/import/parsers/toCurrency';
import { toCustomizableTenor } from 'utils/import/parsers/toCustomizableTenor';
import { toListValue } from 'utils/import/parsers/toListValue';
import { toText } from 'utils/import/parsers/toText';
import { validateNbDecimals } from 'utils/import/validators/nbDecimalsValidator';
import { validateStartDate } from 'utils/import/validators/startDateValidator';
import { ValidationResult } from 'utils/import/validators/validator.model';

import { toDateString } from '../parsers/toDateString';
import { toNumber } from '../parsers/toNumber';
import { toPercentage } from '../parsers/toPercentage';
import { toSalesCounterpart } from '../parsers/toSalesCounterpart';
import { toUnderlyingArray } from '../parsers/toUnderlyingArray';

export type FieldInput = string | number | boolean | Date | undefined;
export type FieldParsed =
  | string
  | number
  | string[]
  | Underlying[]
  | Date
  | ISalesCounterpart
  | TenorFieldData
  | undefined;

export type Parser = (value: unknown) => FieldParsed;
export type Validator = (value: FieldParsed) => ValidationResult;

export type FieldConfig = {
  parser?: Parser;
  validator?: Validator;
};

// Validators ensure values extracted from the file conform to the expected field constraints, same as in UI
export function getFieldConfigByMetadataType(
  fieldMetadata: FieldMetadata,
  translationMap: Record<IntlKey, string>,
): FieldConfig {
  switch (fieldMetadata.type) {
    case 'UnderlyingsField':
    case 'MultipleUnderlyingsField':
    case 'UnderlyingsStrikeField':
      return {
        parser: toUnderlyingArray,
        validator: value => ({ isValid: true, value }),
      };
    case 'TextField':
      return {
        parser: toText,
      };
    case 'NumberField':
    case 'AmountField':
    case 'MultipleUnitsField':
      return {
        parser: value => toNumber(value),
        validator: (value: FieldParsed) => validateNbDecimals(value as number, fieldMetadata.nbDecimals),
      };
    case 'PercentField':
      return {
        parser: value => toPercentage(value),
        validator: (value: FieldParsed) => validateNbDecimals(value as number, fieldMetadata.nbDecimals),
      };
    case 'CurrencyListField':
      return {
        parser: value => toCurrency(value, fieldMetadata as CurrencyListFieldMetadata, translationMap),
      };
    case 'DateField':
      return {
        parser: value => toDateString(value),
        validator: (value: FieldParsed) =>
          validateStartDate(value as string, (fieldMetadata as DateFieldMetadata).startDate),
      };
    case 'ListField':
      return {
        parser: value => toListValue(value, fieldMetadata as ListFieldMetadata, translationMap),
      };
    case 'ClientListField':
      return {
        parser: toSalesCounterpart,
        validator: value => ({ isValid: true, value }),
      };
    case 'CustomizableTenorField':
      return {
        parser: toCustomizableTenor,
      };
    default:
      return {};
  }
}
